import type { AqeTokenConfig } from '@pushly/aqe'

/**
 * =============================================================================
 * PUFFERFISH TOKEN CONFIGURATION
 * =============================================================================
 *
 * Pufferfish-specific token overrides that extend AQE defaults.
 * These values are synchronized with the Pushly color palette defined in:
 *   - aqe/src/style/colors.scss (SCSS variables)
 *   - aqe/src/theme/tokens.config.ts (AQE defaults)
 *
 * @see aqe/.kiro/steering/styles.md for complete color documentation
 *
 * TOKEN MERGE ORDER:
 * 1. AQE defaultTokens (base)
 * 2. pufferfishTokens (this file - overrides AQE)
 * 3. White-label runtime overrides (if applicable)
 */
export const pufferfishTokens: Partial<AqeTokenConfig> = {
    token: {
        // =================================================================
        // COLOR SEED TOKENS - Synchronized with colors.scss
        // =================================================================

        // Primary brand color - used for buttons, switches, tabs, selections
        // Maps to: $color-secondary-100 (teal) - the interactive brand color
        colorPrimary: '#60C3AC',

        // Success color - completed, enabled, running states
        // Maps to: $color-secondary-100 (teal) - consistent with status badges
        colorSuccess: '#60C3AC',

        // Warning color - pending, attention needed
        // Maps to: $color-yellow-100
        colorWarning: '#ffbf00',

        // Error color - failed, destructive actions
        // Maps to: $color-red-100
        colorError: '#f04134',

        // Info color - neutral informational emphasis
        // Maps to: $color-primary-100 (dark gray) - doesn't compete with status colors
        // colorInfo: '#1890ff',

        // Link color - hyperlinks and clickable text
        // Maps to: $color-secondary-100 (teal) - accepted risk for contrast
        colorLink: '#60C3AC',
        colorLinkHover: '#6fcfb8', // Slightly lighter teal on hover

        // Base text color - derives all text color gradients
        // Maps to: $color-black
        colorTextBase: '#303030',
        colorTextSecondary: '#626262',

        // Base background color - derives all background gradients
        // Maps to: $color-white
        colorBgBase: '#ffffff',

        // =================================================================
        // ADDITIONAL DERIVED TOKENS
        // =================================================================

        colorBgContainer: '#ffffff',
        // colorBorderBg: 'rgba(48, 48, 48, 0.5)',
        // boxShadowTertiary: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',

        // =================================================================
        // TYPOGRAPHY TOKENS
        // =================================================================

        fontSize: 14,
        fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif',

        // =================================================================
        // BORDER TOKENS
        // =================================================================

        borderRadius: 6,
        borderRadiusSM: 4,
        borderRadiusLG: 8,
    } as AqeTokenConfig['token'],

    // =========================================================================
    // COMPONENT-SPECIFIC TOKEN OVERRIDES
    // =========================================================================

    components: {
        // Button uses teal as primary color
        Button: {
            colorPrimary: '#60C3AC', // $color-secondary-100
            primaryColor: '#ffffff', // White text on teal buttons
            defaultGhostBorderColor: 'transparent',
        },

        // Menu selection states
        Menu: {
            darkItemBg: '#464d53', // $color-primary-100 for dark sidebar
            itemSelectedBg: 'rgba(96, 195, 172, 0.1)', // Teal with transparency
            itemSelectedColor: '#60C3AC',
            itemPaddingInline: '8px 16px',
            itemHeight: 28,
        },

        // Table
        Table: {
            headerBg: '#fbfafa',
        },

        // Tabs - brand teal for selected/active tab text
        // Note: aria-required-children on Tabs is a known antd 6 upstream issue (deferred).
        // Tab text contrast on off-white backgrounds is accepted brand risk.
        Tabs: {
            inkBarColor: '#60C3AC',
        },

        // Switch uses teal when checked
        Switch: {
            colorPrimary: '#60C3AC',
            colorPrimaryHover: '#6fcfb8', // Slightly lighter teal
        },

        // Layout sidebar background
        Layout: {
            siderBg: '#464d53', // $color-primary-100
            triggerBg: '#464d53',
        },

        // Typography defaults
        Typography: {
            titleMarginBottom: 0,
            // colorTextDescription: '303030ab', // Secondary text — 5.9:1 on white
        },

        // Form spacing
        Form: {
            itemMarginBottom: 16,
        },

        // Card styling
        Card: {
            // borderColor: '#464d53', // Uncomment if needed
        },

        // Notification — compact layout for title-less simple notifications
        Notification: {
            paddingContentHorizontalLG: 16,
        },
    },
}

/**
 * =============================================================================
 * PUFFERFISH CUSTOM TOKENS
 * =============================================================================
 *
 * Extended tokens not in Ant Design's token system.
 * Used for custom components and Insights/analytics charts.
 *
 * These values are synchronized with colors.scss:
 *   - Secondary scale: $color-secondary-100, $color-secondary-90, etc.
 *   - Tertiary scale: $color-tertiary-100, $color-tertiary-90, etc.
 *   - Insights colors: $INSIGHTS_* variables
 */
export const pufferfishCustomTokens = {
    // =========================================================================
    // BRAND COLOR SCALES
    // =========================================================================

    // Secondary (Teal) - Interactive elements
    colorSecondary: '#60C3AC', // $color-secondary-100
    colorSecondary90: '#6fcfb8', // Lighter teal
    colorSecondary70: '#9fdfce', // Even lighter

    // Tertiary (Purple) - Accents & highlights
    colorTertiary: '#722ed1', // $color-tertiary-100
    colorTertiary90: '#8e5dd6', // Lighter purple
    colorTertiary80: '#a47ddb', // Even lighter

    // =========================================================================
    // INSIGHTS/ANALYTICS CHART COLORS
    // Synchronized with $INSIGHTS_* variables in colors.scss
    // =========================================================================

    colorInsightsDelivery: '#555555', // $INSIGHTS_DELIVERY_COLOR
    colorInsightsImpressions: '#0077a0', // $INSIGHTS_IMPS_COLOR
    colorInsightsDR: '#6aa7cc', // $INSIGHTS_DR_COLOR
    colorInsightsClicks: '#722ed1', // $INSIGHTS_CLICKS_COLOR ($color-tertiary-100)
    colorInsightsCTR: '#ac99d1', // $INSIGHTS_CTR_COLOR
    colorInsightsSubs: '#3dbd7d', // $INSIGHTS_SUBS_COLOR
    colorInsightsSR: '#7ebd93', // $INSIGHTS_SR_COLOR
    colorInsightsUnsubs: '#ffbf00', // $INSIGHTS_UNSUBS_COLOR ($color-yellow-100)
    colorInsightsWeb: '#f38454', // $INSIGHTS_WEB_COLOR
    colorInsightsWebClicks: '#f6a37f', // Lighter web color
    colorInsightsIOS: '#4f4f4f', // $INSIGHTS_IOS_COLOR
    colorInsightsIOSClicks: '#6e6e6e', // Lighter iOS color
    colorInsightsAndroid: '#63c1a7', // $INSIGHTS_ANDROID_COLOR
    colorInsightsAndroidClicks: '#63c1a7', // Same as Android
}
